import { Box, CircularProgress, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CACHE_TIME } from '@/config/queryDefaults';
import { helpApi } from './helpApi';
import { helpIndexModules } from './helpIndex';
import { helpPanel, useHelpPanel, type HelpTarget } from './helpPanelStore';
import { HelpSectionCard } from './HelpSectionCard';

interface HelpIndexViewProps {
  language: string;
  onOpen: (target: HelpTarget) => void;
}

/**
 * Every help page the user may see, grouped by module in menu order. A module shows its name and number of pages
 * until it is opened — the only module is open — and the panel keeps which are open.
 */
export function HelpIndexView({ language, onOpen }: HelpIndexViewProps) {
  const { t } = useTranslation();
  const { indexOpen } = useHelpPanel();
  const { data: pages, isLoading } = useQuery({
    queryKey: ['help', 'list', language],
    queryFn: helpApi.list,
    staleTime: CACHE_TIME.METADATA,
  });

  if (isLoading) return <CircularProgress size={20} />;
  const modules = helpIndexModules(pages ?? [], (key, fallback) => t(key, { defaultValue: fallback }));
  if (modules.length === 0) {
    return <Typography variant="body2" color="text.secondary">{t('help.indexEmpty')}</Typography>;
  }

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {t('help.allHelp')}
      </Typography>
      {modules.map((m) => (
        <HelpSectionCard
          key={m.module}
          title={m.label}
          count={m.pages.length}
          countLabel={t('help.pageCount', { count: m.pages.length })}
          expanded={modules.length === 1 || indexOpen.includes(m.module)}
          onToggle={() => helpPanel.toggleIndexModule(m.module)}
        >
          <List dense disablePadding>
            {m.pages.map((page) => (
              <ListItemButton key={page.id} onClick={() => onOpen({ pageId: page.id })}>
                <ListItemText primary={page.title} secondary={page.summary} />
              </ListItemButton>
            ))}
          </List>
        </HelpSectionCard>
      ))}
    </Box>
  );
}
